import { useCallback } from 'react'
import { Skeleton, styled, Typography, useTheme } from '@mui/material'
import { MessageSender } from '@weblite/ts-action/message'
import { checkUserAccessibilityViaForwardWithUserId } from '$logic/user-policy'
import { openUserProfile, openWappProfile } from '$logic/router'
import { openRoom } from '$logic/opened-room'
import { t } from '$i18n'
import { showCanNotAccessViaForwardedMessageSnackbar } from '$comp/snackbars'
import { useSenderName } from '$logic/message/react-hook'
import { MESSAGE_BG_BOX_SHADOW } from '$setup/style/constants'

const RoleTypography = styled(Typography)`
  /* @noflip */
  margin-right: 6px;
`

function SenderName({ sender }: { sender: MessageSender }) {
  const { name, loading } = useSenderName(sender, true)
  const role = sender.type === 'WAPP' ? t('message.tool') : null

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        overflow: 'hidden',
        height: '20px',
      }}
    >
      {loading ? (
        <Skeleton variant="text" width="40px" height="18px" />
      ) : (
        <Typography noWrap color="primary700" variant="subtitle4">
          {name}
        </Typography>
      )}

      {role && (
        <RoleTypography variant="caption1" color="neutral500">
          {role}
        </RoleTypography>
      )}
    </div>
  )
}

const ForwardedFromPrefixTypography = styled(Typography)`
  /* @noflip */
  margin-left: 5px;
  flex-shrink: 0;
`

function ForwardedFromName({
  forwardedFrom,
  fromMe,
}: {
  forwardedFrom: MessageSender
  fromMe: boolean
}) {
  const { name, loading } = useSenderName(forwardedFrom, true)

  const forwardedFromChannel = forwardedFrom.type === 'CHANNEL'
  const forwardedFromWapp = forwardedFrom.type === 'WAPP'

  const onUserClick = useCallback(async () => {
    const accessible = await checkUserAccessibilityViaForwardWithUserId(
      forwardedFrom.id,
    )
    if (accessible) await openUserProfile(forwardedFrom.id)
    else showCanNotAccessViaForwardedMessageSnackbar()
  }, [forwardedFrom])

  const onForwardedFromClick = useCallback(async () => {
    if (forwardedFrom.type === 'USER') await onUserClick()
    if (forwardedFrom.type === 'CHANNEL') await openRoom(forwardedFrom.id)
    if (forwardedFrom.type === 'WAPP') await openWappProfile(forwardedFrom.id)
  }, [forwardedFrom, onUserClick])

  return (
    <div style={{ display: 'flex', overflow: 'hidden', height: '20px', alignItems: 'center', }}>
      <ForwardedFromPrefixTypography
        variant="paragraph3"
        color={fromMe ? 'neutral000' : 'neutral500'}
      >
        {t('message.forwardedFrom')}
      </ForwardedFromPrefixTypography>

      {loading ? (
        <Skeleton
          variant="text"
          width="40px"
          height="18px"
          sx={{ display: 'inline-block', lineHeight: '20px' }}
        />
      ) : name ? (
        <Typography
          noWrap
          color={fromMe ? 'primary300' : 'primary500'}
          variant="paragraph3"
          sx={{ cursor: 'pointer' }}
          onClick={onForwardedFromClick}
        >
          {name}
        </Typography>
      ) : forwardedFromChannel ? (
        <Typography
          noWrap
          color="neutral700"
          variant="subtitle4"
          onClick={onForwardedFromClick}
        >
          {t('common.privateChannel')}
        </Typography>
      ) : forwardedFromWapp ? (
        <Typography
          noWrap
          color="neutral700"
          variant="subtitle4"
          onClick={onForwardedFromClick}
        >
          {t('message.tool')}
        </Typography>
      ) : (
        ''
      )}
    </div>
  )
}

export function SenderInfo({
  sender,
  forwardedFrom,
  fromMe,
  isWapp,
  isChannel,
  showSender = true,
}: {
  sender?: MessageSender
  forwardedFrom?: MessageSender
  fromMe: boolean
  isWapp: boolean
  isChannel: boolean
  showSender?: boolean
}) {
  const linearBackground = useTheme().palette.lightChatSenderGradient
  return (
    <div
      style={{
        maxWidth: '100%',
        width: 'fit-content',
        overflow: 'hidden',
        boxSizing: 'border-box',
        /* @noflip */
        padding: isWapp ? '8px 10px' : '8px 10px 0',
        background: isChannel
          ? 'white'
          : isWapp
          ? fromMe
            ? linearBackground
            : 'white'
          : 'transparent',
        marginBottom: isWapp ? '6px' : 0,
        borderRadius: '12px',
        boxShadow: isWapp ? MESSAGE_BG_BOX_SHADOW : '',
      }}
    >
      {sender && showSender && <SenderName sender={sender} />}
      {forwardedFrom && (
        <ForwardedFromName
          forwardedFrom={forwardedFrom}
          fromMe={fromMe && !isChannel} // NOTE: if it is in channel color will change
        />
      )}
    </div>
  )
}

SenderInfo.defaultProps = {
  sender: null,
  forwardedFrom: null,
}
